/**
 * User + crew-member shapes.
 *
 * Users come from Entra (Microsoft 365). Crew members are a subset of users
 * with field-work-specific metadata (truck assignments, headshot, etc.).
 */
import { z } from 'zod';
import { EmailSchema, IsoDateTimeSchema, PhoneSchema, UuidSchema } from './common.js';
import { RoleSchema, UserStatusSchema } from './enums.js';
// ----------------------------------------------------------------------------
// User (Entra-backed identity)
// ----------------------------------------------------------------------------
export const UserSchema = z.object({
    id: UuidSchema,
    entra_oid: z.string().min(1).describe('Entra Object ID; stable across renames'),
    email: EmailSchema,
    display_name: z.string().min(1),
    given_name: z.string().nullable(),
    family_name: z.string().nullable(),
    role: RoleSchema,
    phone: PhoneSchema.nullable(),
    avatar_url: z.string().url().nullable(),
    is_active: z.boolean().default(true),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
export const UserPublicSchema = UserSchema.pick({
    id: true,
    display_name: true,
    given_name: true,
    family_name: true,
    role: true,
    avatar_url: true,
});
// ----------------------------------------------------------------------------
// Crew member (extends user with field-work metadata)
// ----------------------------------------------------------------------------
export const CrewRoleSchema = z.enum(['lead', 'installer', 'driver', 'trainee']);
export const CrewMemberSchema = z.object({
    id: UuidSchema,
    user_id: UuidSchema,
    crew_role: CrewRoleSchema,
    // DEPRECATED (ADR-0019): vehicles are a shared pool booked per task, not
    // owned by a person. These columns are vestigial — no longer set or shown;
    // a later migration can drop them.
    truck_label: z.string().nullable(),
    vehicle_plate: z.string().nullable(),
    default_capacity_hours: z
        .number()
        .min(0)
        .max(24)
        .default(8)
        .describe("daily default work hours; supervisor can override per day"),
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
});
/** Joined crew + user for dispatch-board rendering. */
export const CrewMemberWithUserSchema = CrewMemberSchema.extend({
    user: UserPublicSchema,
});
// ----------------------------------------------------------------------------
// Create / update payloads
// ----------------------------------------------------------------------------
export const UserUpsertSchema = UserSchema.pick({
    entra_oid: true,
    email: true,
    display_name: true,
    given_name: true,
    family_name: true,
    role: true,
    phone: true,
    avatar_url: true,
});
export const CrewMemberCreateSchema = CrewMemberSchema.pick({
    user_id: true,
    crew_role: true,
    truck_label: true,
    vehicle_plate: true,
    default_capacity_hours: true,
});
/** Supervisor edits to an existing crew member (not the user link). */
export const CrewMemberUpdateSchema = CrewMemberSchema.pick({
    crew_role: true,
    truck_label: true,
    vehicle_plate: true,
    default_capacity_hours: true,
}).partial();
// ----------------------------------------------------------------------------
// Admin user management (RoleMgmt.2)
// ----------------------------------------------------------------------------
export const AdminUserSchema = z.object({
    id: UuidSchema,
    display_name: z.string(),
    email: z.string().nullable(),
    role: RoleSchema,
    status: UserStatusSchema,
    is_active: z.boolean(),
    requested_role: RoleSchema.nullable(),
    /** Per-person notification go-live: true = real external sends even while the
     *  org-wide delivery mode is diverting to admins. */
    notifications_live: z.boolean(),
    created_at: IsoDateTimeSchema,
});
/** Admin change to a user's RBAC role, access status, and/or notification go-live. */
export const AdminUserUpdateSchema = z
    .object({
    role: RoleSchema.optional(),
    status: z.enum(['active', 'denied']).optional(),
    notifications_live: z.boolean().optional(),
})
    .refine((d) => d.role !== undefined || d.status !== undefined || d.notifications_live !== undefined, { message: 'Provide a role, status, and/or notification go-live to change.' });
// ----------------------------------------------------------------------------
// Directory import — add a user straight from the Entra directory (admin only)
// ----------------------------------------------------------------------------
/** One Entra-directory person an admin can import, with a flag for whether they
 *  already exist locally (so the picker can disable re-adds). */
export const DirectoryCandidateSchema = z.object({
    entra_oid: z.string().min(1),
    display_name: z.string(),
    email: z.string(),
    job_title: z.string().nullable(),
    given_name: z.string().nullable(),
    family_name: z.string().nullable(),
    /** The local users.id when this person is already provisioned, else null. */
    existing_user_id: UuidSchema.nullable(),
    existing_status: UserStatusSchema.nullable(),
});
/** Response for GET /admin/directory/search. `configured` is false when no Graph
 *  client secret / consent is set up; `available` is false when Graph is set up
 *  but unreachable right now — both let the UI explain the empty result. */
export const DirectorySearchResponseSchema = z.object({
    configured: z.boolean(),
    available: z.boolean(),
    candidates: z.array(DirectoryCandidateSchema),
});
/** Body for POST /admin/users/from-directory — the admin picks the person (by
 *  Entra oid; the server re-resolves their canonical name/email) and a role. */
export const DirectoryUserCreateSchema = z.object({
    entra_oid: z.string().min(1),
    role: RoleSchema,
});
//# sourceMappingURL=users.js.map